<template>
<div class="user-details-container">
    <!-- Header -->
    <div class="refresh-container">
        <a href="#" class="brand w-nav-brand"><img sizes="84.03125px" alt="" :src="logo" loading="lazy" class="image-389" style="height: 45px;"></a>
        <button @click="fetchUserDetails" class="btn-refresh md-btn md-btn-primary">
            <i class="material-icons">refresh</i> Refresh
        </button>
    </div>

    <div v-if="loading" class="loading">
        <div class="md-loader"></div>
        <p>Loading user details...</p>
    </div>

    <div v-else-if="error" class="error md-card md-card-error">
        <i class="material-icons">error</i>
        <p>{{ error }}</p>
    </div>

    <div v-else-if="user" class="main-content">
        <!-- User Details Card -->
        <div class="user-card md-card">
            <h2 class="md-card-title">{{AppName}} - Update Card</h2>

            <div class="user-info">
                <div class="info-row">
                    <span class="label">Name:</span>
                    <span class="value">{{ user.first_name }} {{ user.last_name }}</span>
                </div>

                <div class="info-row">
                    <span class="label">Email:</span>
                    <span class="value">{{ user.email }}</span>
                </div>

                <div v-if="user.phone" class="info-row">
                    <span class="label">Phone:</span>
                    <span class="value">{{ user.phone }}</span>
                </div>
            </div>
        </div>

        <!-- Update Card Form -->
        <div class="md-card">
            <h3 class="md-card-title">Enter New Card Details</h3>

            <!-- Card Element -->
            <div class="section-divider">
                <h4 class="section-title"><i class="material-icons">credit_card</i> Card Information</h4>
            </div>

            <div class="md-form-group">
                <label>Card Details <span style="color: red;">*</span></label>
                <div v-if="user.is_stripe" id="update-stripe-card-element" class="ns-card-element" ref="updateStripeCardElement"></div>
                <div v-else id="update-card-element" class="ns-card-element" ref="updateCardElement"></div>
            </div>

            <!-- Billing Address Section -->
            <div class="section-divider">
                <h4 class="section-title"><i class="material-icons">location_on</i> Billing Address</h4>
            </div>

            <div class="md-form-group">
                <label>Billing Address <span style="color: red;">*</span></label>
                <google-places-autocomplete :api-key="googleMapsApiKey" @address-updated="getBillingAddress" ref="billingAutocomplete" />
            </div>

            <!-- Success Message -->
            <div v-if="successMessage" class="md-alert md-alert-success">
                <i class="material-icons">check_circle</i>
                <p>{{ successMessage }}</p>
            </div>

            <!-- Error Message -->
            <div v-if="submitError" class="md-alert md-alert-error">
                <i class="material-icons">error</i>
                <p>{{ submitError }}</p>
            </div>

            <!-- Submit Button -->
            <div class="submit-container">
                <button @click="submitCardUpdate" class="md-btn md-btn-success" :disabled="submitting">
                    <i class="material-icons">{{ submitting ? 'hourglass_empty' : 'save' }}</i>
                    {{ submitting ? 'Updating...' : 'Update Card' }}
                </button>
            </div>
        </div>
    </div>

    <div v-else class="not-found">
        <p>User not found</p>
    </div>
</div>
</template>

<script>
import {
    BasisTheory
} from "@basis-theory/basis-theory-js";
import { loadStripe } from "@stripe/stripe-js";

const bt = await new BasisTheory().init(process.env.VUE_APP_BASIS, {
    elements: true
});
const stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY);

import GooglePlacesAutocomplete from './GooglePlacesAutocomplete.vue'

export default {
    name: 'UpdateCard',
    components: {
        GooglePlacesAutocomplete
    },
    data() {
        return {
            user: null,
            loading: true,
            error: null,
            logo: process.env.VUE_APP_LOGO,
            AppName: process.env.VUE_APP_NAME,
            submitting: false,
            successMessage: '',
            submitError: '',
            cardElement: null,
            cardToken: '',
            cardBin: '',
            cardLast4: '',
            cardBrand: '',
            googleMapsApiKey: process.env.VUE_APP_GOOGLE_KEY,
            billingAddress: {
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: ''
            }
        }
    },
    async created() {
        const userId = this.$route.params.id;
        await this.fetchUserDetails(userId);
    },
    mounted() {
        if (!document.querySelector('link[href*="material-icons"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
            document.head.appendChild(link);
        }
    },
    methods: {
        async fetchUserDetails(userId = null) {
            this.loading = true;
            this.error = null;
            const id = userId || this.$route.params.id;

            try {
                const userResponse = await this.$http.get(`/users/${id}`);
                this.user = userResponse.data.user;

                // Mount card element after user loads
                this.$nextTick(() => {
                    setTimeout(() => { this.createCardElement(); }, 100);
                });
            } catch (err) {
                console.error('Error fetching user details:', err);
                this.error = err.response?.data?.message || 'Failed to load user details. Please try again later.';
            } finally {
                this.loading = false;
            }
        },

        async createCardElement() {
            if (this.user && this.user.is_stripe) {
                if (stripe && this.$refs.updateStripeCardElement) {
                    try {
                        const elements = stripe.elements();
                        this.cardElement = elements.create('card');
                        this.cardElement.mount('#update-stripe-card-element');

                        this.cardElement.on("change", (event) => {
                            if (event.brand) {
                                this.cardBrand = event.brand;
                            }
                        });
                    } catch (error) {
                        console.error('Error mounting Stripe card element:', error);
                    }
                }
                return;
            }

            if (bt && this.$refs.updateCardElement) {
                try {
                    this.cardElement = bt.createElement('card');
                    await this.cardElement.mount('#update-card-element');

                    this.cardElement.on("change", (event) => {
                        if (event.cardBrand) {
                            this.cardBrand = event.cardBrand;
                        }
                        if (event.cardBin && event.cardBin.length >= 6) {
                            this.cardBin = event.cardBin;
                        }
                        if (event.cardLast4) {
                            this.cardLast4 = event.cardLast4;
                        }
                    });
                } catch (error) {
                    console.error('Error mounting card element:', error);
                }
            }
        },

        getBillingAddress(address) {
            this.billingAddress = {
                address1: address.street || '',
                address2: address.address_line2 || '',
                city: address.city || '',
                state: address.state || '',
                zip: address.zip || ''
            };
        },

        async submitCardUpdate() {
            if (this.submitting) return;

            this.successMessage = '';
            this.submitError = '';

            // Validate billing address
            if (!this.billingAddress.address1 || !this.billingAddress.city || !this.billingAddress.state || !this.billingAddress.zip) {
                this.submitError = 'Please enter a complete billing address.';
                return;
            }

            this.submitting = true;

            try {
                let tokenId;
                let details;

                if (this.user.is_stripe) {
                    const result = await stripe.createToken(this.cardElement);
                    if (result.error) {
                        this.submitError = result.error.message || 'Invalid card details.';
                        this.submitting = false;
                        return;
                    }
                    tokenId = result.token.id;
                    details = {
                        cardBin: result.token.card.brand,
                        cardBrand: result.token.card.brand,
                        cardLast4: result.token.card.last4
                    };
                } else {
                    const token = await bt.tokens.create({
                        type: "card",
                        data: this.cardElement,
                    });
                    tokenId = token.id;
                    details = {
                        ...token.data,
                        cardBin: this.cardBin,
                        cardBrand: this.cardBrand,
                        cardLast4: this.cardLast4
                    };
                }

                const payload = {
                    userId: this.$route.params.id,
                    token: tokenId,
                    details,
                    billingAddress: {
                        first_name: this.user.first_name || '',
                        last_name: this.user.last_name || '',
                        address1: this.billingAddress.address1,
                        address2: this.billingAddress.address2,
                        city: this.billingAddress.city,
                        state: this.billingAddress.state,
                        zip: this.billingAddress.zip,
                        country: 'US'
                    }
                };

                const response = await this.$http.post('/update-card-user', payload);

                if (response.data.status === 'success') {
                    this.successMessage = 'Card updated successfully!';
                } else {
                    this.submitError = response.data.message || 'Failed to update card.';
                }
            } catch (error) {
                console.error('Error updating card:', error);
                if (error.message && error.message.includes('token')) {
                    this.submitError = 'Invalid card details. Please check and try again.';
                } else {
                    this.submitError = error.response?.data?.message || error.response?.data?.errors || 'Failed to update card. Please try again.';
                }
            } finally {
                this.submitting = false;
            }
        }
    }
}
</script>

<style scoped>
/* Material Design inspired styles */
.user-details-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
    position: relative;
    font-family: 'Roboto', Arial, sans-serif;
}

.refresh-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.btn-refresh {
    display: flex;
    align-items: center;
}

.loading,
.error,
.not-found {
    text-align: center;
    padding: 20px;
}

.md-loader {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top: 4px solid #3498db;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Card Styles */
.md-card {
    background-color: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    transition: box-shadow 0.3s ease;
}

.md-card:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.md-card-title {
    font-size: 1.5rem;
    font-weight: 500;
    color: #333;
    margin-top: 0;
    margin-bottom: 24px;
    text-align: center;
}

.md-card-error {
    background-color: #ffebee;
    color: #d32f2f;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* Alert Styles */
.md-alert {
    padding: 16px;
    border-radius: 4px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
}

.md-alert i {
    margin-right: 12px;
    font-size: 24px;
}

.md-alert-error {
    background-color: #ffebee;
    color: #d32f2f;
    border-left: 4px solid #f44336;
}

.md-alert-success {
    background-color: #E8F5E9;
    color: #2E7D32;
    border-left: 4px solid #4CAF50;
}

.user-info {
    margin-bottom: 0;
}

.info-row {
    display: flex;
    padding: 12px 0;
    border-bottom: 1px solid #eee;
    align-items: flex-start;
}

.info-row:last-child {
    border-bottom: none;
}

.label {
    font-weight: 500;
    min-width: 150px;
    width: 150px;
    color: #555;
    flex-shrink: 0;
}

.value {
    flex: 1;
    color: #333;
    word-break: break-word;
}

/* Button Styles */
.md-btn {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: background-color 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.md-btn i {
    margin-right: 6px;
}

.md-btn:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.md-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
}

.md-btn-primary {
    background-color: #2196F3;
    color: white;
}

.md-btn-success {
    background-color: #4CAF50;
    color: white;
}

/* Form Styles */
.md-form-group {
    margin-bottom: 20px;
}

.md-form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #555;
    font-size: 0.9rem;
}

/* Basis Theory Card Element */
.ns-card-element {
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 12px;
    min-height: 40px;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.ns-card-element:focus-within {
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

/* Section Divider */
.section-divider {
    margin-top: 24px;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e0e0e0;
}

.section-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 500;
    color: #333;
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-title i {
    font-size: 20px;
    color: #2196F3;
}

/* Submit Container */
.submit-container {
    display: flex;
    justify-content: center;
    margin-top: 24px;
}

.submit-container .md-btn {
    padding: 12px 32px;
    font-size: 16px;
}

/* Responsive Design */
@media (max-width: 768px) {
    .user-details-container {
        padding: 15px;
    }

    .refresh-container {
        flex-direction: column;
        gap: 15px;
        align-items: stretch;
    }

    .md-card {
        padding: 16px;
    }

    .info-row {
        flex-direction: column;
        gap: 5px;
    }

    .label {
        min-width: auto;
        width: auto;
        font-weight: 600;
    }
}
</style>
